import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { Icon } from '@iconify/react';
import WorkOrderDetails from './WorkOrderDetails';
import WorkOrderMakeRecurringModal from './WorkOrderMakeRecurringModal';
import WorkOrderRecurrenceModal from './WorkOrderRecurrenceModal';
import { getWorkOrderStatus } from '../api/work-order-statusAPI';
import { fetchData } from '../utils/apiHelpers';
import { getWorkOrderByEventId, updateWorkOrder } from '../api/work-orderAPI';
import WorkOrderStatusModal from './WorkOrderStatusModal';
import EventEditModal from './EventEditModal';
import { formatDateTime, formatIntDateTime, toLocalInputFormat } from '../utils/dateUtils';
import { getEventStatus } from '../api/event-statusAPI';
import { getEventTypes } from '../api/event-typeAPI';
import { getEventResponsibilities } from '../api/event-responsibilityAPI';
import { createEvents, getEventsById, getPaginatedEvents, updateEvents } from '../api/eventsAPI';
import {
  createEventDelay,
  deleteEventDelay,
  getEventDelayByEventId,
  updateEventDelay,
} from '../api/event-delayAPI';
import { getEventDelayCodes } from '../api/event-delay-codeAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useValidator } from '../utils/useValidator';
import { computeDefaultDelayStartDate, validateDelayDateRange } from '../utils/eventDelayRules';

const WorkOrderPanelView = ({
  workOrders = [],
  selectedWorkOrder,
  setSelectedWorkOrder,
  setWorkOrders,
  listContainerRef,
  woLoading = false,
  handleEdit,
  parts = [],
  labour = [],
  services = [],
  equipment = [],
  forms = [],
  actualCosts = {},
  onViewForm,
  onAddForm,
  onExportPdf,
  onCloneWorkOrder,
  onDeleteWorkOrder,
  crew = [],
  notFound = false,
  updateWorkOrders,
  assignmentFilter = 'unassigned',
  onAssignmentFilterChange = () => { },
  panelSortOption = 'date-desc',
  onPanelSortOptionChange = () => { },
}) => {
  const userProfile = useUserProfile();
  const canUpdateWorkOrder = userProfile?.privileges?.includes('work-orders:update');
  const [statuses, setStatuses] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(selectedWorkOrder?.status);
  const [showMakeRecurring, setShowMakeRecurring] = useState(false);
  const [showEditRecurring, setShowEditRecurring] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showAdvancedSchedule, setShowAdvancedSchedule] = useState(false);
  const [showStandardSchedule, setShowStandardSchedule] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [showUnscheduleConfirm, setShowUnscheduleConfirm] = useState(false);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [assigningToEvent, setAssigningToEvent] = useState(false);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [pendingEventStatusId, setPendingEventStatusId] = useState(null);
  const [eventStatusOptions, setEventStatusOptions] = useState([]);
  const [eventResponsibilityOptions, setEventResponsibilityOptions] = useState([]);
  const [defaultEventResponsibilityId, setDefaultEventResponsibilityId] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({});
  const [eventComments, setEventComments] = useState([]);
  const [panelGroupBy, setPanelGroupBy] = useState('assetId');
  const [openGroupKeys, setOpenGroupKeys] = useState({});
  const [eventAssetOptions, setEventAssetOptions] = useState([]);
  const [assignedEventWorkOrders, setAssignedEventWorkOrders] = useState([]);
  const [eventDelays, setEventDelays] = useState([]);
  const [delayCodes, setDelayCodes] = useState([]);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [editingDelay, setEditingDelay] = useState(null);
  const [delayForm, setDelayForm] = useState({
    eventDelayCodeId: '',
    delayStartDate: '',
    delayEndDate: '',
  });
  const delayValidationFields = useMemo(
    () => [
      { field: 'eventDelayCodeId', label: 'Delay Code', required: true },
      { field: 'delayStartDate', label: 'Delay Start Date', required: true, type: 'datetime' },
      { field: 'delayEndDate', label: 'Delay End Date', required: true, type: 'datetime' },
    ],
    [],
  );
  const delayFields = useMemo(
    () => [
      {
        field: 'eventDelayCodeId',
        label: 'Delay Code',
        type: 'select',
        size: 12,
        required: true,
        options: (delayCodes || []).map((code) => ({
          value: code.id,
          label: [code.code, code.name].filter(Boolean).join(' - '),
        })),
      },
      {
        field: 'delayStartDate',
        label: 'Start Date',
        type: 'datetime',
        size: 12,
        required: true,
      },
      {
        field: 'delayEndDate',
        label: 'End Date',
        type: 'datetime',
        size: 12,
        required: true,
      },
    ],
    [delayCodes],
  );
  const {
    validate: validateDelayForm,
    errors: delayErrors,
    setErrors: setDelayErrors,
  } = useValidator(delayValidationFields, delayForm);
  const { renderField: renderDelayField } = useFormBuilder(
    delayFields,
    delayForm,
    setDelayForm,
    delayErrors,
  );
  const [delayDuration, setDelayDuration] = useState(0);

  const calcDelayDuration = useCallback(
    (delay) =>
      delay?.delayEndDate && delay?.delayStartDate
        ? (new Date(delay.delayEndDate) - new Date(delay.delayStartDate)) / (1000 * 60 * 60)
        : 0,
    [],
  );
  const [newEventForm, setNewEventForm] = useState({
    name: '',
    eventTypeId: '',
    plannedStartDate: '',
    plannedEndDate: '',
  });
  const [newEventErrors, setNewEventErrors] = useState({});
  const [savingNewEvent, setSavingNewEvent] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ scheduledAt: '', dueDate: '' });
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [unscheduling, setUnscheduling] = useState(false);

  const sortOptions = [
    { value: 'date-asc', label: 'Date ↑' },
    { value: 'date-desc', label: 'Date ↓' },
    { value: 'number-asc', label: 'Number' },
    { value: 'priority-high', label: 'Priority ↓' },
    { value: 'priority-low', label: 'Priority ↑' },
  ];

  const groupByOptions = [
    { value: 'assetId', label: 'Asset' },
    { value: 'departmentId', label: 'Department' },
    { value: 'taskTypeId', label: 'Task Type' },
    { value: 'priorityId', label: 'Priority' },
    { value: 'statutory', label: 'Compliance' },
    { value: 'breakdown', label: 'Breakdown' },
    { value: 'scheduledAt', label: 'Schedule Date' },
    { value: 'taskId', label: 'Task Link' },
    { value: 'dueDate', label: 'Due Date' },
  ];

  const tabs = [
    { value: 'unassigned', label: 'Unscheduled' },
    { value: 'assigned', label: 'Scheduled' },
    { value: 'complete', label: 'Complete' },
  ];

  const newEventFields = useMemo(
    () => [
      { field: 'name', label: 'Event Name', type: 'text', size: 12, required: true },
      {
        field: 'eventTypeId',
        label: 'Event Type',
        type: 'select',
        size: 6,
        required: true,
        options: eventTypeOptions,
        isClearable: true,
      },
      {
        field: 'plannedStartDate',
        label: 'Planned Start',
        type: 'datetime',
        size: 6,
        required: true,
      },
      {
        field: 'plannedEndDate',
        label: 'Planned End',
        type: 'datetime',
        size: 6,
        required: true,
      },
    ],
    [eventTypeOptions],
  );

  const eventStatusName = useMemo(() => {
    const selectedStatus = eventStatusOptions.find(
      (status) => String(status.value) === String(eventForm?.eventStatus),
    );
    return selectedStatus?.label?.toLowerCase();
  }, [eventForm?.eventStatus, eventStatusOptions]);

  const eventValidationFields = useMemo(() => {
    const isBreakdownEvent = Number(eventForm?.eventTypeId) === 2;
    const isInProgress = eventStatusName === 'in progress';
    const isComplete = eventStatusName === 'complete';
    const isCancelled = eventStatusName === 'cancelled';

    const plannedStartRequired = !(
      (isInProgress && !isBreakdownEvent) || (isComplete && !isBreakdownEvent) || isCancelled
    );

    const plannedEndRequired = !((isComplete && !isBreakdownEvent) || isCancelled);

    const startRequired = (isInProgress || isComplete) && !isCancelled;
    const endRequired = isComplete && !isCancelled;

    return [
      { field: 'name', label: 'Name', required: true },
      { field: 'eventTypeId', label: 'Event Type', required: true },
      { field: 'assetId', label: 'Asset', required: true },
      { field: 'eventLocationId', label: 'Event Location' },
      { field: 'eventStatus', label: 'Status', required: true },
      {
        field: 'plannedStartDate',
        label: 'Planned Start Date',
        required: plannedStartRequired,
        type: 'datetime',
      },
      {
        field: 'plannedEndDate',
        label: 'Planned End Date',
        required: plannedEndRequired,
        type: 'datetime',
      },
      { field: 'startDate', label: 'Start Date', required: startRequired, type: 'datetime' },
      { field: 'endDate', label: 'End Date', required: endRequired, type: 'datetime' },
    ];
  }, [eventForm?.eventStatus, eventForm?.eventTypeId, eventStatusName]);

  const {
    errors: eventFormErrors,
    setErrors: setEventFormErrors,
    validate: validateEventForm,
  } = useValidator(eventValidationFields, eventForm);

  const workOrderStatusOptions = useMemo(
    () =>
      (statuses || []).map((status) => ({
        value: status.id,
        label: status.name,
        bootstrapName: status.colours?.bootstrapName || 'secondary',
      })),
    [statuses],
  );

  const handleEventAssetSearch = useCallback(async () => eventAssetOptions, [eventAssetOptions]);

  const scheduleFields = useMemo(
    () => [
      { field: 'scheduledAt', label: 'Scheduled At', type: 'datetime', size: 6, required: true },
      { field: 'dueDate', label: 'Due Date', type: 'datetime', size: 6, required: true },
    ],
    [],
  );

  const { renderField: renderNewEventField } = useFormBuilder(
    newEventFields,
    newEventForm,
    setNewEventForm,
    newEventErrors,
  );

  const { renderField: renderScheduleField } = useFormBuilder(
    scheduleFields,
    scheduleForm,
    setScheduleForm,
    scheduleErrors,
  );

  useEffect(() => {
    fetchData(getWorkOrderStatus, {}, setStatuses).catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadEventLookups = async () => {
      try {
        const [statusList, typeList, responsibilityList, delayCodesList] = await Promise.all([
          getEventStatus().catch(() => []),
          getEventTypes().catch(() => []),
          getEventResponsibilities().catch(() => []),
          getEventDelayCodes().catch(() => []),
        ]);
        if (!isMounted) return;
        const pendingStatus = (statusList || []).find(
          (status) => status?.name?.toLowerCase() === 'pending',
        );
        setPendingEventStatusId(pendingStatus?.id || null);
        setEventStatusOptions(
          (statusList || []).map((status) => ({ value: status.id, label: status.name })),
        );
        setEventTypeOptions(
          (typeList || []).map((type) => ({ value: type.id, label: type.name })),
        );
        const responsibilityOptions = (responsibilityList || []).map((responsibility) => ({
          value: responsibility.id,
          label: responsibility.name,
          isDefault: responsibility.default === 1 || responsibility.default === true,
        }));
        setEventResponsibilityOptions(responsibilityOptions);
        const defaultOption = responsibilityOptions.find((option) => option.isDefault);
        setDefaultEventResponsibilityId(defaultOption?.value ?? null);
        setDelayCodes(delayCodesList?.data || delayCodesList || []);
      } catch (err) {
        console.error('Error loading scheduling lookups', err);
        if (isMounted) {
          setEventTypeOptions([]);
          setEventStatusOptions([]);
          setPendingEventStatusId(null);
          setEventResponsibilityOptions([]);
          setDefaultEventResponsibilityId(null);
          setDelayCodes([]);
        }
      }
    };
    loadEventLookups();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setCurrentStatus(selectedWorkOrder?.status);
  }, [selectedWorkOrder]);

  useEffect(() => {
    if (delayForm.delayEndDate && delayForm.delayStartDate) {
      const duration =
        (new Date(delayForm.delayEndDate) - new Date(delayForm.delayStartDate)) /
        (1000 * 60 * 60);
      setDelayDuration(Math.abs(duration));
    }
  }, [delayForm.delayEndDate, delayForm.delayStartDate]);

  useEffect(() => {
    if (!selectedWorkOrder) {
      setScheduleForm({ scheduledAt: '', dueDate: '' });
      return;
    }
    setScheduleForm({
      scheduledAt: selectedWorkOrder.scheduledAt || '',
      dueDate: selectedWorkOrder.dueDate || '',
    });
  }, [selectedWorkOrder]);

  useEffect(() => {
    if (!showAddEventModal || !selectedWorkOrder) return;
    const defaultName = [
      selectedWorkOrder.workOrderNumber,
      selectedWorkOrder.workOrderTitle,
    ]
      .filter(Boolean)
      .join(' – ');
    setNewEventForm({
      name: defaultName || '',
      eventTypeId: '',
      plannedStartDate: '',
      plannedEndDate: '',
    });
    setNewEventErrors({});
  }, [showAddEventModal, selectedWorkOrder]);

  useEffect(() => {
    if (!showAdvancedSchedule) {
      setSelectedEventId(null);
      setShowAddEventModal(false);
      return;
    }
    if (!selectedWorkOrder?.assetId) {
      setPendingEvents([]);
      return;
    }
    setEventsLoading(true);
    const params = {
      page: 1,
      pageSize: 100,
      assetId: [selectedWorkOrder.assetId],
    };
    if (pendingEventStatusId) {
      params.eventStatusId = [pendingEventStatusId];
    }
    getPaginatedEvents(params)
      .then((res) => {
        const rows = res?.data || [];
        if (pendingEventStatusId) {
          setPendingEvents(rows);
          return;
        }
        const filtered = rows.filter(
          (event) => event?.eventStatus2?.name?.toLowerCase() === 'pending',
        );
        setPendingEvents(filtered);
      })
      .catch((err) => {
        console.error('Error loading pending events', err);
        setPendingEvents([]);
      })
      .finally(() => {
        setEventsLoading(false);
      });
  }, [showAdvancedSchedule, selectedWorkOrder?.assetId, pendingEventStatusId]);

  const mapWorkOrderStatusToEventStatus = useCallback((statusId) => {
    const mapping = {
      1: 4,
      2: 1,
      4: 2,
      5: 3,
      3: 1,
    };
    return mapping[statusId] ?? null;
  }, []);

  const buildAssetOption = useCallback((workOrder) => {
    if (!workOrder?.assetId) return null;
    const assetNumber = workOrder.asset?.assetNumber || workOrder.asset?.unitNumber;
    const assetDescription = workOrder.asset?.assetDescription;
    const label = [assetNumber, assetDescription].filter(Boolean).join(' - ') || `Asset ${workOrder.assetId}`;
    return { value: workOrder.assetId, label };
  }, []);

  const buildEventFormFromWorkOrder = useCallback(
    (workOrder) => {
      if (!workOrder) return {};
      const plannedStartDate = workOrder.scheduledAt || workOrder.ScheduledAt || '';
      const plannedEndDate = workOrder.dueDate || workOrder.DueDate || '';
      const startDate = workOrder.startedAt || '';
      const endDate = workOrder.completedAt || '';
      return {
        name: workOrder.workOrderTitle || '',
        assetId: workOrder.assetId || '',
        plannedStartDate: plannedStartDate ? toLocalInputFormat(plannedStartDate) : '',
        plannedEndDate: plannedEndDate ? toLocalInputFormat(plannedEndDate) : '',
        startDate: startDate ? toLocalInputFormat(startDate) : '',
        endDate: endDate ? toLocalInputFormat(endDate) : '',
        eventTypeId: workOrder.breakdown ? 2 : 1,
        eventStatus: mapWorkOrderStatusToEventStatus(workOrder.statusId),
        eventResponsibilityId: defaultEventResponsibilityId ?? null,
      };
    },
    [defaultEventResponsibilityId, mapWorkOrderStatusToEventStatus],
  );

  const handleOpenEventModal = useCallback(async () => {
    if (!selectedWorkOrder) return;
    setEventFormErrors({});
    setAssignedEventWorkOrders([]);
    setEventComments([]);
    const assetOption = buildAssetOption(selectedWorkOrder);
    setEventAssetOptions(assetOption ? [assetOption] : []);
    if (selectedWorkOrder.eventId) {
      try {
        const eventData = await getEventsById(selectedWorkOrder.eventId);
        setEditingEvent(eventData);
        setEventForm({
          name: eventData?.name || '',
          eventTypeId: eventData?.eventTypeId || '',
          eventStatus: eventData?.eventStatus || '',
          assetId: eventData?.assetId || '',
          plannedStartDate: toLocalInputFormat(eventData?.plannedStartDate),
          plannedEndDate: toLocalInputFormat(eventData?.plannedEndDate),
          startDate: toLocalInputFormat(eventData?.startDate),
          endDate: toLocalInputFormat(eventData?.endDate),
          systemId: eventData?.systemId ?? null,
          eventLocationId: eventData?.eventLocationId ?? eventData?.eventLocation?.id ?? null,
          eventResponsibilityId:
            eventData?.eventResponsibilityId ?? eventData?.eventResponsibility?.id ?? null,
        });
        if (eventData?.assetId) {
          const assetNumber = eventData.asset?.assetNumber || eventData.asset?.unitNumber;
          const assetDescription = eventData.asset?.assetDescription;
          const label =
            [assetNumber, assetDescription].filter(Boolean).join(' - ') ||
            `Asset ${eventData.assetId}`;
          setEventAssetOptions([{ value: eventData.assetId, label }]);
        }
        const workOrderResponse = await getWorkOrderByEventId(selectedWorkOrder.eventId);
        const workOrderRows = workOrderResponse?.data || workOrderResponse || [];
        const assigned = Array.isArray(workOrderRows) ? workOrderRows : [];
        const ensured = assigned.some((wo) => wo.id === selectedWorkOrder.id)
          ? assigned
          : [...assigned, selectedWorkOrder];
        setAssignedEventWorkOrders(ensured);
        const delaysResponse = await getEventDelayByEventId(selectedWorkOrder.eventId);
        const delays = delaysResponse?.data || delaysResponse || [];
        setEventDelays(delays);
      } catch (err) {
        console.error('Error loading event details', err);
        setEditingEvent(null);
        setEventForm(buildEventFormFromWorkOrder(selectedWorkOrder));
        setAssignedEventWorkOrders([selectedWorkOrder]);
        setEventDelays([]);
      }
    } else {
      setEditingEvent(null);
      setEventForm(buildEventFormFromWorkOrder(selectedWorkOrder));
      setAssignedEventWorkOrders([selectedWorkOrder]);
      setEventDelays([]);
    }
    setShowEventModal(true);
  }, [
    buildAssetOption,
    buildEventFormFromWorkOrder,
    selectedWorkOrder,
    setEventFormErrors,
  ]);

  useEffect(() => {
    if (!showEventModal || editingEvent || !defaultEventResponsibilityId) {
      return;
    }
    setEventForm((prev) => {
      if (prev?.eventResponsibilityId) return prev;
      return {
        ...prev,
        eventResponsibilityId: defaultEventResponsibilityId,
      };
    });
  }, [defaultEventResponsibilityId, editingEvent, showEventModal]);

  const handleSaveEvent = useCallback(async () => {
    const isValid = validateEventForm();
    if (!isValid) return;
    const workOrderIds = Array.from(
      new Set(
        assignedEventWorkOrders
          .map((wo) => Number(wo?.id))
          .filter((id) => Number.isFinite(id)),
      ),
    );
    const payload = {
      name: eventForm.name,
      assetId: Number(eventForm.assetId),
      eventTypeId: Number(eventForm.eventTypeId),
      plannedStartDate: eventForm.plannedStartDate
        ? new Date(eventForm.plannedStartDate).toISOString()
        : null,
      plannedEndDate: eventForm.plannedEndDate
        ? new Date(eventForm.plannedEndDate).toISOString()
        : null,
      startDate: eventForm.startDate ? new Date(eventForm.startDate).toISOString() : null,
      endDate: eventForm.endDate ? new Date(eventForm.endDate).toISOString() : null,
      eventStatus: Number(eventForm.eventStatus) || null,
      systemId:
        eventForm.systemId !== undefined && eventForm.systemId !== null && eventForm.systemId !== ''
          ? Number(eventForm.systemId)
          : null,
      eventLocationId:
        eventForm.eventLocationId !== undefined &&
          eventForm.eventLocationId !== null &&
          eventForm.eventLocationId !== ''
          ? Number(eventForm.eventLocationId)
          : null,
      eventResponsibilityId:
        eventForm.eventResponsibilityId !== undefined &&
          eventForm.eventResponsibilityId !== null &&
          eventForm.eventResponsibilityId !== ''
          ? Number(eventForm.eventResponsibilityId)
          : null,
      workOrderIds,
    };
    if (!editingEvent) {
      const pendingComments = Array.isArray(eventComments) ? eventComments : [];
      const pendingDelays = Array.isArray(eventDelays) ? eventDelays : [];
      if (pendingComments.length) {
        payload.eventComments = pendingComments.map((comment) => ({
          comments: comment?.comments || comment?.comment || '',
          photos: Array.isArray(comment?.photos) ? comment.photos : [],
          userId: comment?.userId ?? comment?.user?.id ?? comment?.user?.userId ?? null,
        }));
      }
      if (pendingDelays.length) {
        payload.eventDelays = pendingDelays.map((delay) => ({
          eventDelayCodeId: delay?.eventDelayCodeId ?? delay?.eventDelayCode?.id ?? null,
          delayStartDate: delay?.delayStartDate ?? null,
          delayEndDate: delay?.delayEndDate ?? null,
        }));
      }
    }
    try {
      let savedEventId = editingEvent?.id || null;
      if (editingEvent) {
        await updateEvents(editingEvent.id, payload);
      } else {
        const created = await createEvents(payload);
        savedEventId = created?.id || savedEventId;
      }
      if (savedEventId && selectedWorkOrder) {
        setSelectedWorkOrder((prev) =>
          prev
            ? {
              ...prev,
              eventId: savedEventId,
            }
            : prev,
        );
        if (typeof setWorkOrders === 'function') {
          setWorkOrders((prev) =>
            prev.map((wo) =>
              wo.id === selectedWorkOrder.id ? { ...wo, eventId: savedEventId } : wo,
            ),
          );
        }
      }
      if (typeof updateWorkOrders === 'function') {
        updateWorkOrders();
      }
      setShowEventModal(false);
      setEditingEvent(null);
    } catch (err) {
      console.error('Error saving event', err);
    }
  }, [
    assignedEventWorkOrders,
    editingEvent,
    eventForm,
    eventComments,
    eventDelays,
    selectedWorkOrder,
    setSelectedWorkOrder,
    setWorkOrders,
    updateWorkOrders,
    validateEventForm,
  ]);

  const handleAddDelay = () => {
    setEditingDelay(null);
    const defaultStartDate = computeDefaultDelayStartDate(eventForm, editingEvent);
    setDelayForm({
      eventDelayCodeId: '',
      delayStartDate: defaultStartDate ? toLocalInputFormat(defaultStartDate, true) : '',
      delayEndDate: '',
    });
    setDelayErrors({});
    setShowDelayModal(true);
  };

  const handleEditDelay = (delay) => {
    setEditingDelay(delay);
    setDelayForm({
      eventDelayCodeId: delay.eventDelayCodeId,
      delayStartDate: toLocalInputFormat(delay.delayStartDate, true),
      delayEndDate: toLocalInputFormat(delay.delayEndDate, true),
    });
    setDelayErrors({});
    setShowDelayModal(true);
  };

  const handleDeleteDelay = async (id) => {
    if (!window.confirm('Delete delay?')) return;
    try {
      if (editingEvent?.id) {
        await deleteEventDelay(id);
        const updated = await getEventDelayByEventId(editingEvent.id);
        const delays = updated?.data || updated || [];
        setEventDelays(delays);
      } else {
        setEventDelays((prev) => (Array.isArray(prev) ? prev.filter((d) => d.id !== id) : []));
      }
    } catch (err) {
      console.error('Error deleting delay', err);
    }
  };

  const handleDelaySave = async () => {
    const isBaseValid = validateDelayForm();
    const customErrors = validateDelayDateRange(
      delayForm.delayStartDate,
      delayForm.delayEndDate,
      eventForm,
      editingEvent,
    );
    if (Object.keys(customErrors).length) {
      setDelayErrors((prev) => ({ ...prev, ...customErrors }));
    }
    if (!isBaseValid || Object.keys(customErrors).length) return;

    const eventStart = eventForm.startDate
      ? new Date(eventForm.startDate)
      : new Date(eventForm.plannedStartDate);
    const eventEnd = eventForm.endDate
      ? new Date(eventForm.endDate)
      : new Date(eventForm.plannedEndDate);
    const delayStart = delayForm.delayStartDate ? new Date(delayForm.delayStartDate) : null;
    const delayEnd = delayForm.delayEndDate ? new Date(delayForm.delayEndDate) : null;

    const durationHours = Math.abs(eventEnd - eventStart) / (1000 * 60 * 60);
    const existingTotal = eventDelays.reduce((sum, d) => sum + calcDelayDuration(d), 0);
    const updatedTotal = editingDelay
      ? existingTotal - calcDelayDuration(editingDelay) + delayDuration
      : existingTotal + delayDuration;
    if (Number.isFinite(durationHours) && updatedTotal >= durationHours) {
      alert('Total delay duration must be less than the event duration');
      return;
    }

    const payload = {
      eventDelayCodeId: Number(delayForm.eventDelayCodeId),
      delayStartDate: delayStart ? delayStart.toISOString() : null,
      delayEndDate: delayEnd ? delayEnd.toISOString() : null,
    };

    try {
      if (editingEvent?.id) {
        const remotePayload = { ...payload, eventsId: editingEvent.id };
        if (editingDelay) {
          await updateEventDelay(editingDelay.id, remotePayload);
        } else {
          await createEventDelay(remotePayload);
        }
        const updated = await getEventDelayByEventId(editingEvent.id);
        const delays = updated?.data || updated || [];
        setEventDelays(delays);
        setShowDelayModal(false);
      } else {
        const delayCode = delayCodes.find(
          (code) => String(code.id) === String(payload.eventDelayCodeId),
        );
        setEventDelays((prev) => {
          const existing = Array.isArray(prev) ? prev : [];
          const updatedDelay = {
            ...editingDelay,
            id: editingDelay?.id ?? Date.now(),
            ...payload,
            eventDelayCode: delayCode || editingDelay?.eventDelayCode || null,
          };
          const next = editingDelay
            ? existing.map((entry) => (entry.id === editingDelay.id ? updatedDelay : entry))
            : [updatedDelay, ...existing];
          return next;
        });
        setShowDelayModal(false);
      }
    } catch (err) {
      console.error('Error saving delay', err);
    }
  };


  const handleJobReadyStatusChange = async (nextValue) => {
    if (!selectedWorkOrder?.id || !canUpdateWorkOrder) return;
    try {
      await updateWorkOrder(selectedWorkOrder.id, { jobReadyStatus: nextValue });
      setSelectedWorkOrder((prev) => (prev ? { ...prev, jobReadyStatus: nextValue } : prev));
      if (typeof setWorkOrders === 'function') {
        setWorkOrders((prev) => prev.map((wo) => (wo.id === selectedWorkOrder.id ? { ...wo, jobReadyStatus: nextValue } : wo)));
      }
    } catch (error) {
      console.error('Failed updating work order job ready status', error);
    }
  };

  const handleStatusChange = async (id) => {
    if (!selectedWorkOrder) return;
    if (id === 2 || id === 4) {
      setPendingStatus(id);
      setShowStatusModal(true);
      return;
    }
    try {
      await updateWorkOrder(selectedWorkOrder.id, { statusId: id });
      const newStatus = statuses.find((s) => s.id === id);
      if (newStatus) {
        setCurrentStatus(newStatus);
        setSelectedWorkOrder((prev) => ({ ...prev, statusId: id, status: newStatus }));
      }
      if (typeof updateWorkOrders === 'function') {
        updateWorkOrders();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getPlanningTypeId = useCallback((workOrder) => {
    if (!workOrder) return null;
    const asset = workOrder.asset || {};
    if (asset.PlanningTypeId != null) return Number(asset.PlanningTypeId);
    if (asset.planningTypeId != null) return Number(asset.planningTypeId);
    if (asset.planningType != null) return Number(asset.planningType);
    if (asset.PlanningType?.id != null) return Number(asset.PlanningType.id);
    if (asset.PlanningType?.typeDescription) {
      const desc = asset.PlanningType.typeDescription.toLowerCase();
      if (desc === 'advanced') return 1;
      if (desc === 'standard') return 2;
      if (desc === 'basic') return 3;
    }
    return null;
  }, []);

  const handleScheduleWorkOrder = () => {
    if (!selectedWorkOrder) return;
    const planningTypeId = getPlanningTypeId(selectedWorkOrder);
    if (planningTypeId === 1) {
      if (!selectedWorkOrder.assetId) {
        console.warn('Cannot schedule an advanced work order without an asset.');
        return;
      }
      setShowAdvancedSchedule(true);
      return;
    }
    if (planningTypeId === 2 || planningTypeId === 3) {
      setScheduleErrors({});
      setShowStandardSchedule(true);
      return;
    }
    setScheduleErrors({});
    setShowStandardSchedule(true);
  };

  const handleUnscheduleWorkOrder = () => {
    if (!selectedWorkOrder) return;
    setShowUnscheduleConfirm(true);
  };

  const handleConfirmUnschedule = async () => {
    if (!selectedWorkOrder) return;
    const planningTypeId = getPlanningTypeId(selectedWorkOrder);
    let payload = null;
    if (planningTypeId === 1) {
      payload = { eventId: null };
    } else if (planningTypeId === 2 || planningTypeId === 3) {
      payload = { scheduledAt: null };
    } else {
      payload = { scheduledAt: null };
    }
    setUnscheduling(true);
    try {
      await updateWorkOrder(selectedWorkOrder.id, payload);
      setSelectedWorkOrder((prev) =>
        prev
          ? {
            ...prev,
            ...payload,
          }
          : prev,
      );
      if (typeof updateWorkOrders === 'function') {
        updateWorkOrders();
      }
      setShowUnscheduleConfirm(false);
    } catch (err) {
      console.error('Error unscheduling work order', err);
    } finally {
      setUnscheduling(false);
    }
  };

  const handleAssignToEvent = async () => {
    if (!selectedWorkOrder || !selectedEventId) return;
    setAssigningToEvent(true);
    try {
      await updateWorkOrder(selectedWorkOrder.id, { eventId: selectedEventId });
      setSelectedWorkOrder((prev) =>
        prev
          ? {
            ...prev,
            eventId: selectedEventId,
          }
          : prev,
      );
      if (typeof updateWorkOrders === 'function') {
        updateWorkOrders();
      }
      setShowAdvancedSchedule(false);
    } catch (err) {
      console.error('Error assigning work order to event', err);
    } finally {
      setAssigningToEvent(false);
    }
  };

  const handleSaveNewEvent = async () => {
    if (!selectedWorkOrder) return;
    const errors = {};
    if (!newEventForm.name) errors.name = 'Required';
    if (!newEventForm.eventTypeId) errors.eventTypeId = 'Required';
    if (!newEventForm.plannedStartDate) errors.plannedStartDate = 'Required';
    if (!newEventForm.plannedEndDate) errors.plannedEndDate = 'Required';
    if (
      newEventForm.plannedStartDate &&
      newEventForm.plannedEndDate &&
      new Date(newEventForm.plannedEndDate) < new Date(newEventForm.plannedStartDate)
    ) {
      errors.plannedEndDate = 'Planned end must be after planned start';
    }
    setNewEventErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (!selectedWorkOrder.assetId) {
      console.error('Cannot create an event without an associated asset.');
      return;
    }
    const payload = {
      name: newEventForm.name,
      assetId: Number(selectedWorkOrder.assetId),
      eventTypeId: Number(newEventForm.eventTypeId),
      plannedStartDate: new Date(newEventForm.plannedStartDate).toISOString(),
      plannedEndDate: new Date(newEventForm.plannedEndDate).toISOString(),
      eventStatus: pendingEventStatusId != null ? Number(pendingEventStatusId) : null,
    };
    setSavingNewEvent(true);
    try {
      const created = await createEvents(payload);
      const newEventId = created?.id;
      if (newEventId) {
        await updateWorkOrder(selectedWorkOrder.id, { eventId: newEventId });
        setSelectedWorkOrder((prev) =>
          prev
            ? {
              ...prev,
              eventId: newEventId,
            }
            : prev,
        );
        if (typeof updateWorkOrders === 'function') {
          updateWorkOrders();
        }
      }
      setShowAddEventModal(false);
      setShowAdvancedSchedule(false);
    } catch (err) {
      console.error('Error creating event for work order', err);
    } finally {
      setSavingNewEvent(false);
    }
  };

  const handleSaveScheduleDates = async () => {
    if (!selectedWorkOrder) return;
    const errors = {};
    if (!scheduleForm.scheduledAt) errors.scheduledAt = 'Required';
    if (!scheduleForm.dueDate) errors.dueDate = 'Required';
    if (
      scheduleForm.scheduledAt &&
      scheduleForm.dueDate &&
      new Date(scheduleForm.dueDate) < new Date(scheduleForm.scheduledAt)
    ) {
      errors.dueDate = 'Due date must be after the scheduled date';
    }
    setScheduleErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const payload = {
      scheduledAt: new Date(scheduleForm.scheduledAt).toISOString(),
      dueDate: new Date(scheduleForm.dueDate).toISOString(),
    };
    setSavingSchedule(true);
    try {
      await updateWorkOrder(selectedWorkOrder.id, payload);
      setSelectedWorkOrder((prev) =>
        prev
          ? {
            ...prev,
            ...payload,
          }
          : prev,
      );
      if (typeof updateWorkOrders === 'function') {
        updateWorkOrders();
      }
      setShowStandardSchedule(false);
    } catch (err) {
      console.error('Error updating work order schedule', err);
    } finally {
      setSavingSchedule(false);
    }
  };

  const shouldShowUnscheduleOption =
    Boolean(selectedWorkOrder?.scheduledAt) &&
    !(
      selectedWorkOrder?.assetId &&
      getPlanningTypeId(selectedWorkOrder) === 1 &&
      !selectedWorkOrder?.eventId
    );
  const isAdvancedAsset = useMemo(() => {
    if (!selectedWorkOrder) return false;
    if (getPlanningTypeId(selectedWorkOrder) === 1) return true;
    const assetType =
      selectedWorkOrder.asset?.assetType?.name ||
      selectedWorkOrder.asset?.assetType ||
      selectedWorkOrder.assetType;
    if (assetType) {
      return String(assetType).toLowerCase() === 'advanced';
    }
    const planningType =
      selectedWorkOrder.asset?.planningType?.typeDescription ||
      selectedWorkOrder.asset?.PlanningType?.typeDescription;
    if (planningType) {
      return String(planningType).toLowerCase() === 'advanced';
    }
    return false;
  }, [getPlanningTypeId, selectedWorkOrder]);

  const getDateRangeMeta = useCallback((dateValue, emptyLabel, keyPrefix, pastLabel) => {
    if (!dateValue) {
      return { key: `${keyPrefix}-none`, label: emptyLabel, sortOrder: 99 };
    }
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return { key: `${keyPrefix}-none`, label: emptyLabel, sortOrder: 99 };
    }
    const now = new Date();
    if (parsedDate < now) {
      return { key: `${keyPrefix}-past`, label: pastLabel, sortOrder: 0 };
    }
    const diffDays = Math.ceil((parsedDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const ranges = [7, 14, 30, 60, 90, 180];
    const rangeIndex = ranges.findIndex((limit) => diffDays <= limit);
    if (rangeIndex !== -1) {
      const range = ranges[rangeIndex];
      return { key: `${keyPrefix}-${range}`, label: `Next ${range} Days`, sortOrder: rangeIndex + 1 };
    }
    return { key: `${keyPrefix}-beyond`, label: 'Beyond 180 Days', sortOrder: 98 };
  }, []);

  const getGroupMeta = useCallback(
    (workOrder) => {
      if (!workOrder) return { key: 'unknown', label: 'Unknown' };
      if (panelGroupBy === 'statutory') {
        const isCompliance = Number(workOrder.statutory) === 1;
        return {
          key: isCompliance ? 'compliance-yes' : 'compliance-no',
          label: isCompliance ? 'Compliance' : 'Other',
        };
      }
      if (panelGroupBy === 'breakdown') {
        const isBreakdown = Number(workOrder.breakdown) === 1;
        return {
          key: isBreakdown ? 'breakdown-yes' : 'breakdown-no',
          label: isBreakdown ? 'Breakdown' : 'Planned',
        };
      }
      if (panelGroupBy === 'scheduledAt') {
        return getDateRangeMeta(
          workOrder.scheduledAt || workOrder.ScheduledAt,
          'Not Scheduled',
          'scheduled',
          'Overdue',
        );
      }
      if (panelGroupBy === 'taskId') {
        const hasTask = Boolean(workOrder.taskId);
        return {
          key: hasTask ? 'task-linked' : 'task-unlinked',
          label: hasTask ? 'Linked to a Task' : 'Not Linked to a Task',
        };
      }
      if (panelGroupBy === 'dueDate') {
        return getDateRangeMeta(
          workOrder.dueDate || workOrder.DueDate,
          'No Due Date',
          'due',
          'Past Due',
        );
      }
      if (panelGroupBy === 'taskTypeId') {
        const name =
          workOrder.taskType?.taskTypeName ||
          workOrder.taskTypeName ||
          workOrder.taskType?.name ||
          '';
        const id = workOrder.taskTypeId;
        return {
          key: id ? `taskType-${id}` : 'taskType-none',
          label: name || (id ? `Task Type ${id}` : 'No Task Type'),
        };
      }
      if (panelGroupBy === 'departmentId') {
        const name = workOrder.department?.name || workOrder.departmentName || '';
        const id = workOrder.departmentId;
        return {
          key: id ? `department-${id}` : 'department-none',
          label: name || (id ? `Department ${id}` : 'No Department'),
        };
      }
      if (panelGroupBy === 'priorityId') {
        const name = workOrder.priority?.name || workOrder.priority || '';
        const id = workOrder.priorityId;
        return {
          key: id ? `priority-${id}` : 'priority-none',
          label: name || (id ? `Priority ${id}` : 'No Priority'),
        };
      }
      const assetNumber =
        workOrder.asset?.assetNumber || workOrder.asset?.unitNumber || '';
      const id = workOrder.assetId;
      return {
        key: id ? `asset-${id}` : 'asset-none',
        label: assetNumber || (id ? `Asset ${id}` : 'No Asset'),
      };
    },
    [getDateRangeMeta, panelGroupBy],
  );

  const groupedWorkOrders = useMemo(() => {
    const groups = [];
    const groupMap = new Map();
    workOrders.forEach((workOrder) => {
      const meta = getGroupMeta(workOrder);
      if (!groupMap.has(meta.key)) {
        const group = { ...meta, items: [] };
        groupMap.set(meta.key, group);
        groups.push(group);
      }
      groupMap.get(meta.key).items.push(workOrder);
    });
    if (panelGroupBy === 'scheduledAt' || panelGroupBy === 'dueDate') {
      groups.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    }
    return groups;
  }, [getGroupMeta, panelGroupBy, workOrders]);

  useEffect(() => {
    const nextOpenKeys = {};
    groupedWorkOrders.forEach((group) => {
      nextOpenKeys[group.key] = true;
    });
    setOpenGroupKeys(nextOpenKeys);
  }, [groupedWorkOrders, panelGroupBy]);
  return (
    <>
      <div className="row pb-16 pe-16 ps-24" style={{ height: 'calc(100vh - 210px)' }}>
        <div className="col-xxl-4 col-md-5 h-100 d-flex flex-column rounded-3 bg-white p-0 m-0" style={{ borderColor: "#eee", borderWidth: "2px" }}>
          <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills mb-16">
            {tabs.map((tab) => (
              <li className="nav-item" role="presentation" key={tab.value}>
                <button
                  type="button"
                  className={`nav-link ${assignmentFilter === tab.value ? 'active' : ''}`}
                  onClick={() => {
                    if (assignmentFilter !== tab.value) {
                      onAssignmentFilterChange(tab.value);
                    }
                  }}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="d-flex align-items-center justify-content-between px-16 pb-16">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" htmlFor="work-order-panel-sort">
                  Sort By
                </label>
                <select
                  id="work-order-panel-sort"
                  className="form-select form-select-sm w-auto"
                  value={panelSortOption}
                  onChange={(e) => onPanelSortOptionChange(e.target.value)}
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0" htmlFor="work-order-panel-group">
                  Group By
                </label>
                <select
                  id="work-order-panel-group"
                  className="form-select form-select-sm w-auto"
                  value={panelGroupBy}
                  onChange={(e) => {
                    setPanelGroupBy(e.target.value);
                    if (typeof updateWorkOrders === 'function') {
                      updateWorkOrders();
                    }
                  }}
                >
                  {groupByOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div
            className="table-responsive flex-grow-1 overflow-auto scroll"
            ref={listContainerRef}
          >
            <table className="table table-hover mb-0 panel-table">
              <thead>
                <tr>
                  <th style={{ display: 'none' }}></th>
                </tr>
              </thead>
              <tbody>
                {workOrders.length === 0 && !woLoading && (
                  <tr className='border-bottom'>
                    <td className="text-center py-4 rounded-0">No Work Orders</td>
                  </tr>
                )}
                {groupedWorkOrders.map((group) => (
                  <React.Fragment key={group.key}>
                    <tr className="table-light">
                      <td>
                        <button
                          type="button"
                          className="btn btn-link d-flex align-items-center gap-2 px-8 py-8 text-decoration-none text-black"
                          onClick={() =>
                            setOpenGroupKeys((prev) => ({
                              ...prev,
                              [group.key]: !prev[group.key],
                            }))
                          }
                        >
                          <Icon
                            icon={openGroupKeys[group.key] ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                          />
                          <span className="fw-semibold">{group.label}</span>
                          <span className="text-muted text-sm">({group.items.length})</span>
                        </button>
                      </td>
                    </tr>
                    {openGroupKeys[group.key] &&
                      group.items.map((row) => {
                        const isRecurring = row.workOrderScheduleId;
                        const isFromTask = row.taskId;
                        const linkedToEvent = row.eventId;
                        const isBreakdown = row.breakdown;
                        const isWorkOrderModified =
                          row.isModified === 1;
                        const isOverdue =
                          row.dueDate &&
                          new Date(row.dueDate) < new Date() &&
                          ![4, 5, 6].includes(row.statusId);
                        const isPotentialWarranty = row.potentialWarranty === true || row.potentialWarranty === 1;
                        return (
                          <tr
                            key={row.id}
                            onClick={() => setSelectedWorkOrder(row)}
                            className={selectedWorkOrder?.id === row.id ? 'notification-selected' : ''}
                          >
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="d-flex align-items-center justify-content-center flex-shrink-0 me-12"
                                  style={{ width: '60px' }}
                                >
                                  <div className="d-flex align-items-center">
                                    <span
                                      className="bg-primary rounded-circle d-inline-block me-6"
                                      style={{
                                        width: '8px',
                                        height: '8px',
                                        visibility: isWorkOrderModified ? 'hidden' : 'visible',
                                      }}
                                    />
                                    <div className={`alert alert-${selectedWorkOrder?.statusId === 5 ? 'danger' : 'primary'} p-10 mb-0 ms-6`}>
                                      <Icon
                                        icon={row.status?.icon || 'mage:folder-open'}
                                        className={`text-${selectedWorkOrder?.statusId === 5 ? 'danger' : 'primary'}`}
                                        style={{ fontSize: '24px' }}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* Middle content - fills remaining space */}
                                <div className="flex-grow-1 px-2">
                                  <div className="text-sm">
                                    #{row.workOrderNumber}
                                  </div>
                                  <div className="fw-bold">
                                    {row.workOrderTitle}
                                  </div>
                                  <div className="text-muted text-md">
                                    {row.assetId
                                      ? `${row.asset?.assetNumber || ''} - ${row.asset?.assetDescription || ''}`
                                      : row.areaId
                                        ? `${row.area?.code || ''} - ${row.area?.name || ''}`
                                        : '-'}
                                  </div>
                                </div>

                                {/* Right icon block - fixed 75px */}
                                <div
                                  className="d-flex align-items-center justify-content-end gap-2 flex-shrink-0"
                                  style={{ width: '75px' }}
                                >
                                  {Number(row.jobReadyStatus) === 1 && (
                                    <Icon
                                      icon="mdi:check"
                                      className="text-success text-xl"
                                    />
                                  )}
                                  {isBreakdown === 1 && (
                                    <Icon
                                      icon="ic:baseline-bolt"
                                      className="text-danger text-xl"
                                    />
                                  )}
                                  {isOverdue && (
                                    <Icon
                                      icon="mage:exclamation-circle"
                                      className="text-danger text-xl"
                                    />
                                  )}
                                  {isPotentialWarranty && (
                                    <span
                                      className="text-danger fw-bold"
                                      style={{ fontSize: '14px', lineHeight: 1 }}
                                      title="Potential Warranty Claim"
                                    >
                                      W
                                    </span>
                                  )}
                                  {linkedToEvent && (
                                    <Icon
                                      icon="mdi:calendar-month-outline"
                                      className="text-primary text-xl"
                                    />
                                  )}
                                  {isRecurring && (
                                    <Icon
                                      icon="mdi:backup-restore"
                                      className="text-primary text-xl"
                                    />
                                  )}
                                  {isFromTask && (
                                    <Icon
                                      icon="mdi:calendar-task-outline"
                                      className="text-primary text-xl"
                                    />
                                  )}
                                </div>

                              </div>
                            </td>

                          </tr>
                        );
                      })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {woLoading && (
              <div className="text-center py-2">
                <span className="spinner-border spinner-border-sm" />
              </div>
            )}
          </div>
        </div>
        <div className="col-xxl-8 col-md-7 h-100 d-flex flex-column m-0">
          {selectedWorkOrder && (
            <div
              className="card flex-grow-1 d-flex flex-column overflow-hidden"
              style={{ border: 'solid #eee 2px', borderRadius: '8px' }}
            >
              <div className="card-header flex-shrink-0 card border-0">
                <div className="row align-items-center">
                  <div className="col-9">
                    <h5 className="text-xl mb-0 d-flex align-items-center gap-2">
                      #{selectedWorkOrder.workOrderNumber} - {selectedWorkOrder.workOrderTitle}
                    </h5>
                  </div>

                  <div className="col-3 d-flex justify-content-end gap-2">
                    {/*{selectedWorkOrder.workOrderScheduleId && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setShowEditRecurring(true)}
                      >
                        <Icon icon="mdi:refresh" className="text-xxl me-6" style={{ position: "relative", top: "-1px" }} />
                        Edit Recurring
                      </button>
                    )}*/}
                    {/*{!selectedWorkOrder.taskId && !selectedWorkOrder.workOrderScheduleId && selectedWorkOrder.workOrderStatus !== 4 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm my-6"
                        onClick={() => setShowMakeRecurring(true)}
                      >
                        <Icon icon="mdi:backup-restore" className="text-xl me-6" style={{ position: 'relative', top: '-1px' }} />
                        Make Recurring
                      </button>
                    )}*/}
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm my-8"
                      onClick={() => handleEdit && handleEdit(selectedWorkOrder)}
                    >
                      <Icon icon="mdi:pencil" className="text-lg me-6" style={{ position: 'relative', top: '-1px' }} />
                      Edit
                    </button>
                    {isAdvancedAsset &&
                      !(selectedWorkOrder.statusId === 4 && selectedWorkOrder.eventId === null) && (
                        <span className="d-none d-xxl-inline">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm my-8"
                            onClick={handleOpenEventModal}
                          >
                            <Icon
                              icon="mdi:calendar-month-outline"
                              className="text-lg"
                            />
                            {/*<span className="ms-6">
                              {selectedWorkOrder.eventId ? 'View Event' : '+ Add to Event'}
                            </span>*/}
                          </button>
                        </span>
                      )}

                    <div className="dropdown">
                      <button
                        type="button"
                        className="btn text-primary-600 hover-text-primary px-18 show border-0 text-xxl py-4 my-6"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <Icon
                          icon="mdi:dots-vertical"
                          className='text-primary'
                          style={{ position: 'relative', top: '-5px' }}
                        />
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end mt-12">
                        {/* <li>
                          <button
                            type="button"
                            className="dropdown-item py-12"
                            onClick={() => handleEdit && handleEdit(selectedWorkOrder)}
                          >
                            Edit Work Order
                          </button>
                        </li> */}
                        {isAdvancedAsset &&
                          !(selectedWorkOrder.statusId === 4 && selectedWorkOrder.eventId === null) && (
                            <li>
                              <button
                                type="button"
                                className="dropdown-item py-12"
                                onClick={handleOpenEventModal}
                              >
                                + Add To Event
                              </button>
                            </li>)}
                        {selectedWorkOrder.statusId !== 4 && (
                          <>
                            {!selectedWorkOrder.scheduledAt && (
                              <li>
                                <button
                                  type="button"
                                  className="dropdown-item py-12"
                                  onClick={handleScheduleWorkOrder}
                                >
                                  Schedule Work Order
                                </button>
                              </li>
                            )}

                            {shouldShowUnscheduleOption && (
                              <li>
                                <button
                                  type="button"
                                  className="dropdown-item py-12"
                                  onClick={handleUnscheduleWorkOrder}
                                >
                                  Unschedule Work Order
                                </button>
                              </li>
                            )}
                          </>
                        )}
                        {selectedWorkOrder.statusId !== 4 && selectedWorkOrder?.taskId === null && (
                          <li>
                            <button
                              type="button"
                              className="dropdown-item py-12"
                              onClick={() =>
                                setShowMakeRecurring(true)
                              }
                            >
                              Make Recurring
                            </button>
                          </li>
                        )}
                        <li>
                          <button
                            type="button"
                            className="dropdown-item py-12"
                            onClick={() =>
                              onCloneWorkOrder && onCloneWorkOrder(selectedWorkOrder)
                            }
                          >
                            Clone Work Order
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="dropdown-item py-12"
                            onClick={() =>
                              onExportPdf && onExportPdf(selectedWorkOrder)
                            }
                          >
                            Export to PDF
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="dropdown-item text-danger py-12"
                            onClick={() =>
                              onDeleteWorkOrder && onDeleteWorkOrder(selectedWorkOrder.id)
                            }
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    </div>

                  </div>
                </div>
              </div>
              <div className="card-body overflow-auto flex-grow-1">
                <WorkOrderDetails
                  workOrder={selectedWorkOrder}
                  crew={crew}
                  statuses={statuses}
                  currentStatus={currentStatus}
                  onStatusChange={handleStatusChange}
                  parts={parts}
                  labour={labour}
                  services={services}
                  equipment={equipment}
                  forms={forms}
                  actualCosts={actualCosts}
                  onViewForm={onViewForm}
                  onAddForm={onAddForm}
                  canUpdateWorkOrder={canUpdateWorkOrder}
                  onJobReadyStatusChange={handleJobReadyStatusChange}
                />
              </div>
            </div>
          )}
          {!selectedWorkOrder && notFound && (
            <div className="d-flex justify-content-center align-items-center flex-grow-1">
              Work Order Not Found
            </div>
          )}
        </div>
      </div>
      {showAdvancedSchedule && selectedWorkOrder && (
        <div className="modal modal-lg modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Schedule Work Order</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAdvancedSchedule(false)}
                />
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-start mb-16">
                  <div>
                    <div className="fw-semibold">
                      {[selectedWorkOrder.workOrderNumber, selectedWorkOrder.workOrderTitle]
                        .filter(Boolean)
                        .join(' – ')}
                    </div>
                    <div className="text-muted text-sm">
                      Asset:{' '}
                      {selectedWorkOrder.asset
                        ? [
                          selectedWorkOrder.asset.assetNumber,
                          selectedWorkOrder.asset.assetDescription,
                        ]
                          .filter(Boolean)
                          .join(' – ')
                        : selectedWorkOrder.assetId}
                    </div>
                    <div className="mt-16">
                      Select an event to add this work order to, or create a new event.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    onClick={() => {
                      setNewEventErrors({});
                      setShowAddEventModal(true);
                    }}
                  >
                    <Icon icon="ic:baseline-plus" className="text-lg" />
                    Add To New Event
                  </button>
                </div>
                {eventsLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                ) : pendingEvents.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <th>Name</th>
                          <th>Planned Start</th>
                          <th>Planned End</th>
                          <th>Event Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingEvents.map((event) => (
                          <tr key={event.id}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedEventId === event.id}
                                onChange={() =>
                                  setSelectedEventId((prev) =>
                                    prev === event.id ? null : event.id,
                                  )
                                }
                              />
                            </td>
                            <td>{event.name || `Event ${event.id}`}</td>
                            <td>{formatIntDateTime(event.plannedStartDate)}</td>
                            <td>{formatIntDateTime(event.plannedEndDate)}</td>
                            <td>{event.eventType?.name || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-info mb-0">
                    No pending events are available for this asset.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAdvancedSchedule(false)}
                  disabled={assigningToEvent}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignToEvent}
                  disabled={!selectedEventId || assigningToEvent}
                >
                  {assigningToEvent ? 'Assigning…' : 'Assign to Selected Event'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddEventModal && selectedWorkOrder && (
        <div className="modal modal-lg modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Event</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddEventModal(false);
                    setNewEventErrors({});
                  }}
                  disabled={savingNewEvent}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3 text-muted text-sm">
                  Asset:{' '}
                  {selectedWorkOrder.asset
                    ? [
                      selectedWorkOrder.asset.assetNumber,
                      selectedWorkOrder.asset.assetDescription,
                    ]
                      .filter(Boolean)
                      .join(' – ')
                    : selectedWorkOrder.assetId}
                </div>
                <div className="row g-3">{newEventFields.map(renderNewEventField)}</div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddEventModal(false);
                    setNewEventErrors({});
                  }}
                  disabled={savingNewEvent}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveNewEvent}
                  disabled={savingNewEvent}
                >
                  {savingNewEvent ? 'Saving…' : 'Save Event'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showStandardSchedule && selectedWorkOrder && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Schedule Work Order</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowStandardSchedule(false);
                    setScheduleErrors({});
                  }}
                  disabled={savingSchedule}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">{scheduleFields.map(renderScheduleField)}</div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowStandardSchedule(false);
                    setScheduleErrors({});
                  }}
                  disabled={savingSchedule}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveScheduleDates}
                  disabled={savingSchedule}
                >
                  {savingSchedule ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUnscheduleConfirm && selectedWorkOrder && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Remove Schedule</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowUnscheduleConfirm(false)}
                  disabled={unscheduling}
                />
              </div>
              <div className="modal-body">
                Do you want to remove the schedule from Work Order{' '}
                {[selectedWorkOrder.workOrderNumber, selectedWorkOrder.workOrderDescription]
                  .filter(Boolean)
                  .join(' ')}
                ?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUnscheduleConfirm(false)}
                  disabled={unscheduling}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmUnschedule}
                  disabled={unscheduling}
                >
                  {unscheduling ? 'Removing…' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selectedWorkOrder && (
        <WorkOrderMakeRecurringModal
          show={showMakeRecurring}
          onClose={() => setShowMakeRecurring(false)}
          workOrder={selectedWorkOrder}
          onSaved={(id) => {
            setSelectedWorkOrder((prev) => ({
              ...prev,
              workOrderScheduleId: id,
            }));
            if (typeof updateWorkOrders === 'function') {
              updateWorkOrders();
            }
          }}
        />
      )}
      {selectedWorkOrder?.workOrderScheduleId && (
        <WorkOrderRecurrenceModal
          show={showEditRecurring}
          onClose={() => setShowEditRecurring(false)}
          scheduleId={selectedWorkOrder.workOrderScheduleId}
          onSaved={() => {
            if (typeof updateWorkOrders === 'function') {
              updateWorkOrders();
            }
          }}
        />
      )}
      {showEventModal && selectedWorkOrder && (
        <EventEditModal
          show={showEventModal}
          onClose={() => {
            setShowEventModal(false);
            setEditingEvent(null);
          }}
          onSave={handleSaveEvent}
          editingEvent={editingEvent}
          newEvent={eventForm}
          setNewEvent={setEventForm}
          newEventErrors={eventFormErrors}
          statusOptions={eventStatusOptions}
          eventTypeOptions={eventTypeOptions}
          eventResponsibilityOptions={eventResponsibilityOptions}
          showEventResponsibility
          assetOptions={eventAssetOptions}
          handleAssetSearch={handleEventAssetSearch}
          assignedWorkOrders={assignedEventWorkOrders}
          workOrderStatuses={workOrderStatusOptions}
          disableAssetSelection
          eventDelays={eventDelays}
          onAddDelay={handleAddDelay}
          onEditDelay={handleEditDelay}
          onDeleteDelay={handleDeleteDelay}
          formatDateTime={formatDateTime}
          eventComments={eventComments}
          onEventCommentsChange={setEventComments}
        />
      )}
      {showDelayModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingDelay ? 'Edit Delay' : 'Add Delay'}</h5>
                <button className="btn-close" onClick={() => setShowDelayModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {delayFields.map(renderDelayField)}
                </div>
                <div className="mt-3 text-muted">
                  Duration: {delayDuration.toFixed(2)} hours
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDelayModal(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleDelaySave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showStatusModal && selectedWorkOrder && (
        <WorkOrderStatusModal
          show={showStatusModal}
          statusId={pendingStatus}
          workOrder={selectedWorkOrder}
          onClose={() => {
            setShowStatusModal(false);
            setPendingStatus(null);
          }}
          onSaved={(payload) => {
            const newStatus = statuses.find((s) => s.id === pendingStatus);
            if (newStatus) {
              setCurrentStatus(newStatus);
              setSelectedWorkOrder((prev) => ({
                ...prev,
                ...payload,
                statusId: pendingStatus,
                status: newStatus,
              }));
            }
            if (typeof updateWorkOrders === 'function') {
              updateWorkOrders();
            }
          }}
        />
      )}
    </>
  );
};

export default WorkOrderPanelView;
